import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { ScenarioForm } from "@/components/scenarios/ScenarioForm";
import { RouteLoading } from "@/components/layout/RouteStatus";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { readQuestionnaireMCQ, readRolePlayConfig } from "@/lib/powtoon-be/types";
import { saveScenarioWithContext } from "@/lib/scenarios/saveScenario";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { toast } from "sonner";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import { activeQuestionnaireId, activeRolePlayId } from "@/lib/scenarios/questionnaireIds";
import { canEditScenario, COLLEAGUE_EDIT_ENABLED } from "@/lib/scenarios/editAccess";

export function TrainerEditScenario() {
  const { scenarioId = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = usePowtoonAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["scenario", scenarioId],
    queryFn: () => powtoonBE.scenarios.get(scenarioId),
  });
  const isOwner = !!data && !!user && data.owner === user.powtoonUserId;
  // Non-owners need a colleague `can_edit` share to open the editor.
  const sharesQuery = useQuery({
    queryKey: ["scenario-shares", scenarioId],
    queryFn: () => powtoonBE.scenarios.listShares(scenarioId),
    enabled: COLLEAGUE_EDIT_ENABLED && !!data && !!user && !isOwner,
    retry: false,
  });
  const currentQuestionnaireId = activeQuestionnaireId(data);
  const questionnaireQuery = useQuery({
    queryKey: ["questionnaire", scenarioId, currentQuestionnaireId],
    queryFn: () => powtoonBE.questionnaires.get(scenarioId, currentQuestionnaireId!),
    enabled: !!currentQuestionnaireId,
  });
  const currentRoleplayId = activeRolePlayId(data);
  const roleplayQuery = useQuery({
    queryKey: ["roleplay", scenarioId, currentRoleplayId],
    queryFn: () => powtoonBE.roleplays.get(scenarioId, currentRoleplayId!),
    enabled: !!currentRoleplayId,
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => powtoonBE.scenarios.remove(scenarioId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["scenarios"] });
      toast.success("Live avatar deleted");
      trackEvent({
        category: ANALYTICS_CATEGORIES.SCENARIO,
        action: ANALYTICS_ACTIONS.SUCCESS,
        ga4EventName: "scenario_deleted",
        extraData: { scenario_id: scenarioId },
      });
      navigate("/trainer");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to delete");
      trackEvent({
        category: ANALYTICS_CATEGORIES.SCENARIO,
        action: ANALYTICS_ACTIONS.ERROR,
        ga4EventName: "scenario_delete_failed",
        extraData: { scenario_id: scenarioId, error: err.message },
      });
    },
  });

  if (isLoading) return <RouteLoading />;
  if (currentQuestionnaireId && questionnaireQuery.isLoading) return <RouteLoading />;
  if (currentRoleplayId && roleplayQuery.isLoading) return <RouteLoading />;
  if (!data) return <div className="p-8">Not found.</div>;
  if (user && !isOwner && sharesQuery.isLoading) return <RouteLoading />;
  if (user && !canEditScenario(data, user.powtoonUserId, sharesQuery.data)) {
    return <Navigate to={`/learn/${scenarioId}`} replace />;
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-4">
        <Link to="/trainer">
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Back to live avatars
          </Button>
        </Link>
      </div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Edit your Live Avatar</h1>
        <div className="flex items-center gap-2">
          {isOwner && (
            <Button
              variant="outline"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
          <Button type="submit" form="scenario-form" className="gap-1.5">
            <Save className="h-4 w-4" />
            Save changes
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this live avatar?</AlertDialogTitle>
            <AlertDialogDescription>
              “{data.name}” will be permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ScenarioForm
        initial={data}
        initialQuestionnaire={
          questionnaireQuery.data ? readQuestionnaireMCQ(questionnaireQuery.data) : null
        }
        initialRoleplay={
          roleplayQuery.data ? readRolePlayConfig(roleplayQuery.data) : null
        }
        submitLabel="Save changes"
        onCancel={() => navigate("/trainer")}
        onSubmit={async (input, meta) => {
          await saveScenarioWithContext(input, meta, { kind: "update", scenarioId });
          await qc.invalidateQueries({ queryKey: ["scenarios"] });
          await qc.invalidateQueries({ queryKey: ["scenario", scenarioId] });
          toast.success("Saved");
          trackEvent({
            category: ANALYTICS_CATEGORIES.SCENARIO,
            action: ANALYTICS_ACTIONS.SUCCESS,
            ga4EventName: "scenario_edited",
            extraData: { scenario_id: scenarioId },
          });
          navigate("/trainer");
        }}
      />
    </div>
  );
}